import React, { useMemo, useCallback, memo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { closeModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import StandardModal from '../StandardModal';
import { FormInput, FormSelect } from 'components/Form';
import { isValidEmail } from 'utils/validators';

const AddOrganizationMemberModal = memo(({ options }) => {
	const dispatch = useDispatch();
	const { actions } = useOrganizations();

	const organizationId = options?.organizationId;
	const onSuccess = options?.onSuccess;

	// React Hook Form
	const { control, handleSubmit, formState } = useForm({
		defaultValues: {
			email: '',
			role: 'member',
		},
		mode: 'onChange',
	});

	// Memoized role options
	const roleOptions = useMemo(
		() => [
			{ value: 'admin', label: 'Admin - Full management access' },
			{ value: 'member', label: 'Member - Standard access' },
			{ value: 'viewer', label: 'Viewer - Read-only access' },
		],
		[],
	);

	// Form submit handler
	const onSubmit = useCallback(
		async (data) => {
			try {
				await actions.addMember({
					id: organizationId,
					data,
				});

				dispatch(
					showToast({
						title: 'Success',
						body: 'Member added successfully',
						type: 'success',
					}),
				);

				if (onSuccess) {
					onSuccess();
				}

				dispatch(closeModal());
			} catch (error) {
				dispatch(
					showToast({
						title: 'Error',
						body: error.message || 'Failed to add member',
						type: 'error',
					}),
				);
			}
		},
		[organizationId, actions, dispatch, onSuccess],
	);

	const handleCancel = useCallback(() => {
		dispatch(closeModal());
	}, [dispatch]);

	return (
		<StandardModal
			title="Add Organization Member"
			size="md"
			onClose={handleCancel}
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="modal-body">
					<FormInput
						name="email"
						control={control}
						type="email"
						label="Email Address"
						placeholder="Enter member's email address"
						required
						validate={{
							isValidEmail: (value) =>
								isValidEmail(value) || 'Please enter a valid email address',
						}}
					/>

					<FormSelect
						name="role"
						control={control}
						label="Role"
						options={roleOptions}
						required
						helperText="Admin users can manage members and settings. Members have standard access. Viewers can only view data."
					/>

					<div className="alert alert-info mt-3">
						<i className="bi bi-info-circle me-2"></i>
						<strong>Note:</strong> The user will be notified via email and can
						accept the invitation to join this organization.
					</div>
				</div>

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleCancel}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={!formState.isValid}
					>
						<i className="bi bi-person-plus me-2" />
						Add Member
					</button>
				</div>
			</form>
		</StandardModal>
	);
});

AddOrganizationMemberModal.displayName = 'AddOrganizationMemberModal';

export default AddOrganizationMemberModal;
